import Form, { useForm } from "components/materials/Form";
import classes from "./classes.module.scss";
import Button, { EButtonVariant, EButtonColor } from "components/elements/Button";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import { useCallback, useEffect, useState } from "react";
import DragAndDropFile from "components/materials/Form/DragAndDropFileElement";
import { useProjectContext } from "components/pages/FeaturedProjects/contexts/Project";
import ProjectUpdateRequestResource from "common/resources/Project/ProjectUpdateRequestResource";
import { EAnchorStatus } from "common/enums/Anchor/EAnchorStatus";
import { ELang } from "common/enums/User/ELang";
import ProjectService from "services/ProjectService";
import { container } from "tsyringe";
import CreativeSourceService from "services/projects/CreativeSourceService";
import CreativeSourceResponseResource from "common/resources/Project/Config/CreativeSource/CreativeSourceResponseResource";
import { useProjectId } from "hooks/useProjectId";
import { PlusIcon } from "@heroicons/react/24/solid";
import CreativeSource from "./CreativeSource";
import { ECreativeSourceModal, useCreativeSourceModal } from "./CreativeSourceModal";
import CreateCreativeSourceModal from "./CreativeSourceModal/CreateCreativeSourceModal";
import EditCreativeSourceModal from "./CreativeSourceModal/EditCreativeSourceModal";
import CreativeSourceRequestResource from "common/resources/Project/Config/CreativeSource/CreativeSourceRequestResource";
import { generateUniqueId } from "utils/UniqueIdUtils";
import CreativeSourceToCreate from "./CreativeSourceToCreate";
import EditCreativeSourceToCreateModal from "./CreativeSourceModal/EditCreativeSourceToCreateModal";
import { upsert } from "common/utils/array";
import { match } from "ts-pattern";
import { EToastType, useToast } from "contexts/Toast";

const creativeSourceService = container.resolve(CreativeSourceService);
const projectService = container.resolve(ProjectService);

type FormData = {
	[key: string]: unknown;
};

export enum ECreativeSourceOperation {
	CREATE = "CREATE",
	EDIT = "EDIT",
	DELETE = "DELETE",
}

export type CreativeSourceCreateOperation = {
	creativeSourceId: string;
	operation: ECreativeSourceOperation.CREATE;
	request: CreativeSourceRequestResource;
	image?: File;
};

export type CreativeSourceEditOperation = {
	creativeSourceId: string;
	operation: ECreativeSourceOperation.EDIT;
	request: CreativeSourceRequestResource;
	image?: File;
};

export type CreativeSourceDeleteOperation = {
	creativeSourceId: string;
	operation: ECreativeSourceOperation.DELETE;
};

const isCreateOrEditOperation = (operation: CreativeSourceOperation): operation is CreativeSourceCreateOperation | CreativeSourceEditOperation => {
	return operation.operation === ECreativeSourceOperation.CREATE || operation.operation === ECreativeSourceOperation.EDIT;
};

export type CreativeSourceOperation = CreativeSourceCreateOperation | CreativeSourceEditOperation | CreativeSourceDeleteOperation;

export default function Documents() {
	const { showToast } = useToast();
	const { operations, upsertOperation, removeOperation } = useCreativeSourceOperations();
	const { project } = useProjectContext();
	const { isModalOpen, onCloseModal, onOpenCreateModal, onOpenEditModal, selectedCreativeSource, onOpenEditToCreateModal, selectedCreativeSourceToCreate } =
		useCreativeSourceModal();
	const { firstCreativeSource, upsertCreativeSource, removeCreativeSource } = useCreativeSources();
	const { formRef, errors, handleError, clearErrors } = useForm();
	const [isLoading, setIsLoading] = useState(false);
	const [hasDataChanged, setHasDataChanged] = useState(false);
	const isProjectAnchored = project.scenario?.anchor?.status === EAnchorStatus.VERIFIED_ON_CHAIN;
	const operationToCreateOrEdit = operations.filter(isCreateOrEditOperation);
	const alreadyOneOperationOrCreativeSource = operationToCreateOrEdit.length > 0 || firstCreativeSource;

	const onDataChange = useCallback(() => {
		if (hasDataChanged) return;
		setHasDataChanged(true);
	}, [hasDataChanged]);

	const validateProjectData = useCallback(() => {
		const projectResource = ProjectUpdateRequestResource.hydrate<ProjectUpdateRequestResource>({
			title: project.title,
			pitch: project.pitch,
			theme: project.theme,
			format: project.format,
			purposes: project.purpose.map((p) => p.purpose),
			lang: I18n.getLang().toLocaleUpperCase() as ELang,
			discord: project.discord ?? undefined,
			instagram: project.instagram ?? undefined,
			x: project.x ?? undefined,
			linkedin: project.linkedin ?? undefined,
		});

		return projectResource.validateOrReject();
	}, [project]);

	const onSubmit = useCallback(
		async (formData: FormData) => {
			try {
				setIsLoading(true);
				setHasDataChanged(false);
				clearErrors();
				const projectData = await validateProjectData();
				const scenario = (formData["scenario"] as FileList)?.[0];
				if (!isProjectAnchored) {
					await projectService.updateProject(project.id, projectData, { scenario });
				}
				await Promise.all(
					operations.map((operation) => {
						return match(operation)
							.with({ operation: ECreativeSourceOperation.CREATE }, (operation) =>
								creativeSourceService.create(project.id, operation.request, operation.image).then(upsertCreativeSource),
							)
							.with({ operation: ECreativeSourceOperation.EDIT }, (operation) =>
								creativeSourceService.update(project.id, operation.creativeSourceId, operation.request, operation.image).then(upsertCreativeSource),
							)
							.with({ operation: ECreativeSourceOperation.DELETE }, (operation) =>
								creativeSourceService.delete(project.id, operation.creativeSourceId).then(() => removeCreativeSource({ id: operation.creativeSourceId })),
							)
							.exhaustive();
					}),
				);
				operations.forEach(removeOperation);
			} catch (error) {
				handleError(error);
			} finally {
				showToast({ message: I18n.asset.pages.professional_space.tabs.information.saved, type: EToastType.SUCCESS });
				setIsLoading(false);
			}
		},
		[
			handleError,
			clearErrors,
			setIsLoading,
			setHasDataChanged,
			validateProjectData,
			project,
			isProjectAnchored,
			operations,
			upsertCreativeSource,
			removeCreativeSource,
			removeOperation,
			showToast,
		],
	);

	const onCreateOperation = useCallback(
		(request: CreativeSourceRequestResource, image?: File) => {
			upsertOperation({
				creativeSourceId: generateUniqueId(),
				operation: ECreativeSourceOperation.CREATE,
				request,
				image,
			});
			onCloseModal();
		},
		[upsertOperation, onCloseModal],
	);

	const onEditOperation = useCallback(
		(creativeSource: CreativeSourceCreateOperation | CreativeSourceEditOperation) => {
			upsertOperation({
				creativeSourceId: creativeSource.creativeSourceId,
				operation: creativeSource.operation,
				request: creativeSource.request,
				image: creativeSource.image,
			});
			onCloseModal();
		},
		[upsertOperation, onCloseModal],
	);

	const onEditCreativeSource = useCallback(
		(creativeSource: Pick<CreativeSourceResponseResource, "id">, request: CreativeSourceRequestResource, image?: File) => {
			upsertOperation({
				creativeSourceId: creativeSource.id,
				operation: ECreativeSourceOperation.EDIT,
				request,
				image,
			});
			removeCreativeSource({ id: creativeSource.id });
			onCloseModal();
		},
		[upsertOperation, onCloseModal, removeCreativeSource],
	);

	const onDeleteCreativeSource = useCallback(
		(deletedCreativeSource: CreativeSourceResponseResource) => {
			upsertOperation({
				creativeSourceId: deletedCreativeSource.id,
				operation: ECreativeSourceOperation.DELETE,
			});
			removeCreativeSource(deletedCreativeSource);
		},
		[upsertOperation, removeCreativeSource],
	);

	return (
		<>
			<Form className={classes["root"]} onChange={onDataChange} formRef={formRef} onSubmit={onSubmit} errors={errors}>
				<div className={classes["header-container"]}>
					<Typography type="h3" weight="bold">
						{I18n.asset.pages.professional_space.tabs.information.sub_tabs.documents}
					</Typography>
					<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} isLoading={isLoading}>
						{I18n.asset.pages.professional_space.tabs.information.save}
					</Button>
				</div>

				<div className={classes["form"]}>
					<DragAndDropFile
						disabled={isProjectAnchored}
						name="scenario"
						accept={[".pdf"]}
						sub_description=".pdf"
						description={I18n.asset.pages.professional_space.tabs.information.project_info.drag_and_drop}
						label={I18n.asset.pages.professional_space.tabs.information.project_info.scenario}
						keyErrorName={"sha256"}
					/>

					<div className={classes["creative-source-container"]}>
						<div className={classes["title-container"]}>
							<Typography type="p" weight="bold" size="large">
								{I18n.asset.pages.professional_space.tabs.information.creative_source.title}
							</Typography>
							<Typography type="p" weight="regular" size="medium">
								{I18n.asset.pages.professional_space.tabs.information.creative_source.description}
							</Typography>
						</div>
						{!alreadyOneOperationOrCreativeSource && (
							<Button variant={EButtonVariant.OUTLINED} color={EButtonColor.PRIMARY} startIcon={<PlusIcon />} onClick={onOpenCreateModal}>
								{I18n.asset.pages.professional_space.tabs.information.creative_source.add}
							</Button>
						)}

						{operationToCreateOrEdit.map((operationToCreate) => (
							<CreativeSourceToCreate
								key={operationToCreate.creativeSourceId}
								creativeSource={operationToCreate}
								onEdit={onOpenEditToCreateModal}
								onDelete={removeOperation}
							/>
						))}
						{firstCreativeSource && <CreativeSource creativeSource={firstCreativeSource} onEdit={onOpenEditModal} onDelete={onDeleteCreativeSource} />}
					</div>
				</div>
			</Form>

			{isModalOpen(ECreativeSourceModal.CREATE_CREATIVE_SOURCE) && <CreateCreativeSourceModal onClose={onCloseModal} onCreate={onCreateOperation} />}
			{isModalOpen(ECreativeSourceModal.EDIT_CREATIVE_SOURCE) && selectedCreativeSource && (
				<EditCreativeSourceModal onClose={onCloseModal} onEdit={onEditCreativeSource} creativeSource={selectedCreativeSource} />
			)}
			{isModalOpen(ECreativeSourceModal.EDIT_CREATIVE_SOURCE_TO_CREATE) && selectedCreativeSourceToCreate && (
				<EditCreativeSourceToCreateModal onClose={onCloseModal} onEdit={onEditOperation} creativeSourceToCreate={selectedCreativeSourceToCreate} />
			)}
		</>
	);
}

const useCreativeSourceOperations = () => {
	const [operations, setOperations] = useState<CreativeSourceOperation[]>([]);

	const upsertOperation = useCallback((operation: CreativeSourceOperation) => {
		setOperations((operations) => upsert(operations, operation, (o) => o.creativeSourceId === operation.creativeSourceId));
		return operation;
	}, []);

	const removeOperation = useCallback((deletedOperation: Pick<CreativeSourceOperation, "creativeSourceId">) => {
		setOperations((operations) => operations.filter((o) => o.creativeSourceId !== deletedOperation.creativeSourceId));
		return deletedOperation;
	}, []);

	return { upsertOperation, removeOperation, operations };
};

const useCreativeSources = () => {
	const { projectId } = useProjectId();
	const [creativeSources, setCreativeSources] = useState<CreativeSourceResponseResource[]>([]);

	const upsertCreativeSource = useCallback((creativeSource: CreativeSourceResponseResource) => {
		setCreativeSources((creativeSources) => upsert(creativeSources, creativeSource, (c) => c.id === creativeSource.id));
		return creativeSource;
	}, []);

	const removeCreativeSource = useCallback((deletedCreativeSource: Pick<CreativeSourceResponseResource, "id">) => {
		setCreativeSources((creativeSources) => creativeSources.filter((c) => c.id !== deletedCreativeSource.id));
		return deletedCreativeSource;
	}, []);

	useEffect(() => {
		creativeSourceService.getAllByProjectId(projectId).then(setCreativeSources);
	}, [projectId]);

	return { removeCreativeSource, upsertCreativeSource, firstCreativeSource: creativeSources[0] };
};
